import React, { useState, useMemo, useEffect } from 'react';
import {
  View,
  Text,
  TouchableOpacity,
  FlatList,
  TextInput,
  TouchableWithoutFeedback,
  Pressable,
  StyleSheet,
  Image,
} from 'react-native';
import { getCountries, getCountryCallingCode } from 'libphonenumber-js';
import { makeStyles } from '../hooks/MakeStylesHook';
import ChevronDownIcon from './Icons/ChevronDownIcon';
import { useTheme } from 'react-native-paper';

// Cache for keeping track of loaded flag items
const flagImageCache = {};

// Create a global object to store preloaded flag images
const preloadedFlags = {};

// Flags are small PNGs from flagcdn.com, fetched only for the rows on screen.
// Territories that share a calling code but have no flag of their own map to
// the flag that represents them.
const specialCountryMappings = {
  // Specific mappings for territories with calling codes
  AC: 'sh', // Ascension Island -> Saint Helena (uses SH flag)
  TA: 'sh', // Tristan da Cunha -> Saint Helena
  SJ: 'no', // Svalbard & Jan Mayen -> Norway
  XK: 'xk', // Kosovo (has its own flag but may not be in all libraries)
  GS: 'gs', // South Georgia
  AX: 'ax', // Åland Islands
  BL: 'bl', // Saint Barthélemy
  MF: 'mf', // Saint Martin
  // Add more special cases as needed
};

// New optimized flag component with static placeholder while loading
const OptimizedFlag = React.memo(
  ({ countryCode, style }) => {
    const [loaded, setLoaded] = React.useState(false);
    const theme = useTheme();
    const code = countryCode?.toUpperCase();

    const mappedCode = code
      ? specialCountryMappings[code] || code.toLowerCase()
      : '';
    const flagUrl = `https://flagcdn.com/w80/${mappedCode}.png`;

    // This helps prevent layout shift
    return (
      <View style={[style, { position: 'relative', overflow: 'hidden' }]}>
        {/* Static placeholder that's always visible until image loads */}
        <View
          style={[
            StyleSheet.absoluteFill,
            {
              backgroundColor: theme.colors.borderColor,
              justifyContent: 'center',
              alignItems: 'center',
              borderRadius: 2,
              zIndex: loaded ? -1 : 1,
            },
          ]}
        >
          <Text
            style={{
              fontSize: 8,
              fontWeight: '600',
              color: theme.colors.secondaryText,
            }}
          >
            {code}
          </Text>
        </View>

        {/* The actual flag image */}
        <Image
          source={{ uri: flagUrl }}
          style={style}
          onLoad={() => {
            preloadedFlags[mappedCode] = true;
            setLoaded(true);
          }}
        />
      </View>
    );
  },
  (prevProps, nextProps) => prevProps.countryCode === nextProps.countryCode
);

// Simplified renderFlag function that uses the OptimizedFlag component
const renderFlag = (countryCode, style) => {
  if (!countryCode) {
    return <View style={[style, { backgroundColor: '#e0e0e0' }]} />;
  }
  return <OptimizedFlag countryCode={countryCode} style={style} />;
};

// Create a memoized country item component to prevent unnecessary re-renders
const MemoizedCountryItem = React.memo(
  ({ country, isSelected, onPress, styles, showCallingCode }) => {
    return (
      <TouchableOpacity
        style={[styles.countryItem, isSelected && styles.selectedItem]}
        onPress={onPress}
      >
        {renderFlag(country.code, styles.countryFlag)}
        <Text style={[styles.countryName, isSelected && { fontWeight: '500' }]}>
          {country.name}
          {showCallingCode ? ` (+${country.callingCode})` : ''}
        </Text>
      </TouchableOpacity>
    );
  },
  (prevProps, nextProps) => {
    // Only re-render if these props changed
    return (
      prevProps.country.code === nextProps.country.code &&
      prevProps.isSelected === nextProps.isSelected &&
      prevProps.showCallingCode === nextProps.showCallingCode
    );
  }
);

// Preload flag function to be called in useEffect
const preloadFlag = (countryCode) => {
  if (!countryCode) return;

  const code = countryCode.toUpperCase();
  const mappedCode = specialCountryMappings[code] || code.toLowerCase();
  if (preloadedFlags[mappedCode]) return; // Already preloaded

  const flagUrl = `https://flagcdn.com/w80/${mappedCode}.png`;

  // Use appropriate preloading method based on environment
  if (typeof Image !== 'undefined' && typeof Image.prefetch === 'function') {
    // React Native environment
    Image.prefetch(flagUrl)
      .then(() => {
        preloadedFlags[mappedCode] = true;
      })
      .catch(() => {
        // Silently handle errors
      });
  } else if (typeof document !== 'undefined') {
    // Web environment
    const img = new Image();
    img.onload = () => {
      preloadedFlags[mappedCode] = true;
    };
    img.src = flagUrl;
  }
};

const CountryPicker = ({
  onSelectCountry,
  selectedCountry,
  label = 'Country/Region',
  error = false,
  errorText = '',
  customStyles = {},
  showCallingCode = true,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [visibleCountries, setVisibleCountries] = useState([]);
  const theme = useTheme();
  const styles = useStyles();

  // Set India as default country when component mounts
  useEffect(() => {
    if (!selectedCountry) {
      onSelectCountry('IN');
    }

    // Add style to override input outline
    if (typeof document !== 'undefined') {
      const style = document.createElement('style');
      style.textContent = `
        input:focus {
          outline: none !important;
          border-color: ${theme.colors.borderColor} !important;
          box-shadow: none !important;
        }
      `;
      document.head.appendChild(style);
    }
  }, []);

  // Preload only the selected country flag
  useEffect(() => {
    if (selectedCountry) {
      // Add selected country to cache to preload its flag
      flagImageCache[selectedCountry] = true;
    }
  }, [selectedCountry]);

  // Create a memoized array of country data with full names
  const countryData = useMemo(() => {
    const countries = getCountries();
    const displayNames = new Intl.DisplayNames(['en'], { type: 'region' });

    return countries.map((country) => ({
      code: country,
      name: displayNames.of(country) || country,
      callingCode: getCountryCallingCode(country),
    }));
  }, []);

  // Filter countries based on search query
  const filteredCountries = useMemo(() => {
    const query = searchQuery.toLowerCase().trim();
    if (!query) return countryData;

    return countryData.filter(
      (country) =>
        country.name.toLowerCase().includes(query) || // Search by full name
        country.code.toLowerCase().includes(query) || // Search by country code
        country.callingCode.includes(query) // Search by calling code
    );
  }, [countryData, searchQuery]);

  // Track which items are visible in the list
  const onViewableItemsChanged = React.useCallback(({ viewableItems }) => {
    const visible = viewableItems.map((item) => item.item.code);
    // Add visible countries to cache
    visible.forEach((code) => {
      flagImageCache[code] = true;
    });
    setVisibleCountries(visible);
  }, []);

  // Viewability config for FlatList
  const viewabilityConfig = {
    itemVisiblePercentThreshold: 50,
    waitForInteraction: false,
    minimumViewTime: 100,
  };

  const viewabilityConfigCallbackPairs = React.useRef([
    { viewabilityConfig, onViewableItemsChanged },
  ]);

  // Preload flags for selected country and visible countries
  useEffect(() => {
    if (selectedCountry) {
      preloadFlag(selectedCountry);
    }

    // Preload visible countries
    visibleCountries.forEach((code) => {
      preloadFlag(code);
    });
  }, [selectedCountry, visibleCountries]);

  const renderCountryItem = ({ item }) => {
    const isSelected = selectedCountry === item.code;

    // Preload the flag for this country when it's rendered
    if (!flagImageCache[item.code]) {
      preloadFlag(item.code);
      flagImageCache[item.code] = true;
    }

    return (
      <MemoizedCountryItem
        country={item}
        isSelected={isSelected}
        onPress={() => {
          onSelectCountry(item.code);
          setIsOpen(false);
          setSearchQuery('');
        }}
        styles={styles}
        showCallingCode={showCallingCode}
      />
    );
  };

  // Get display name for selected country
  const selectedCountryName = selectedCountry
    ? new Intl.DisplayNames(['en'], { type: 'region' }).of(selectedCountry)
    : null;

  const handleOutsideClick = () => {
    setIsOpen(false);
    setSearchQuery('');
  };

  const toggleDropdown = () => {
    setIsOpen(!isOpen);
  };

  return (
    <View style={styles.container}>
      <Text style={styles.label}>{label}</Text>
      <TouchableOpacity
        onPress={toggleDropdown}
        style={[styles.inputContainer, error && styles.error, customStyles]}
      >
        <View style={styles.selectedCountry}>
          {selectedCountry ? (
            <>
              {renderFlag(selectedCountry, styles.countryFlag)}
              <Text style={styles.countryName}>
                {selectedCountryName || selectedCountry}
                {showCallingCode
                  ? ` (+${getCountryCallingCode(selectedCountry)})`
                  : ''}
              </Text>
            </>
          ) : (
            <Text style={styles.placeHolderText}>Select a country</Text>
          )}
        </View>
        <View>
          <ChevronDownIcon
            width={20}
            height={20}
            color={theme.colors.placeHolderText}
          />
        </View>
      </TouchableOpacity>

      {error && errorText && <Text style={styles.errorText}>{errorText}</Text>}

      {isOpen && (
        <>
          <TouchableWithoutFeedback onPress={handleOutsideClick}>
            <View style={styles.backdrop} />
          </TouchableWithoutFeedback>
          <View style={styles.dropdownContainer}>
            <TextInput
              style={[styles.searchInput, { outlineWidth: 0 }]}
              placeholder="Search countries..."
              placeholderTextColor={theme.colors.placeHolderText}
              value={searchQuery}
              onChangeText={setSearchQuery}
              selectionColor={theme.colors.secondaryText}
            />
            <FlatList
              data={filteredCountries}
              keyExtractor={(item) => item.code}
              style={styles.countryList}
              showsVerticalScrollIndicator={false}
              contentContainerStyle={styles.flatListContent}
              renderItem={renderCountryItem}
              nestedScrollEnabled={true}
              scrollEnabled={true}
              initialNumToRender={20}
              maxToRenderPerBatch={30}
              updateCellsBatchingPeriod={30}
              windowSize={15}
              removeClippedSubviews={true}
              getItemLayout={(data, index) => ({
                length: 48,
                offset: 48 * index,
                index,
              })}
              keyboardShouldPersistTaps="handled"
              viewabilityConfigCallbackPairs={
                viewabilityConfigCallbackPairs.current
              }
            />
          </View>
        </>
      )}
    </View>
  );
};

const useStyles = makeStyles((theme) => ({
  container: {
    width: '100%',
    marginVertical: 5,
    position: 'relative',
    zIndex: 10000,
  },
  inputContainer: {
    height: 53,
    width: '100%',
    justifyContent: 'space-between',
    alignItems: 'center',
    flexDirection: 'row',
    borderWidth: 1,
    borderColor: theme.colors.borderColor,
    borderRadius: 5,
    paddingHorizontal: 12,
    backgroundColor: theme.colors.onBackground,
  },
  selectedCountry: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  countryFlag: {
    width: 24,
    height: 24,
    marginRight: 8,
    borderRadius: 2,
    overflow: 'hidden',
  },
  placeholderFlag: {
    backgroundColor: theme.colors.borderColor,
    width: 24,
    height: 24,
    borderRadius: 2,
    justifyContent: 'center',
    alignItems: 'center',
  },
  countryName: {
    fontSize: 13,
    fontWeight: '400',
    color: theme.colors.secondaryText,
  },
  dropdownContainer: {
    position: 'absolute',
    top: '100%',
    left: 0,
    right: 0,
    backgroundColor: theme.colors.onBackground,
    borderWidth: 1,
    borderColor: theme.colors.borderColor,
    borderRadius: 8,
    marginTop: 4,
    maxHeight: 200,
    zIndex: 10001,
    elevation: 5,
    shadowColor: '#000',
    shadowOffset: {
      width: 0,
      height: 2,
    },
    shadowOpacity: 0.25,
    shadowRadius: 3.84,
  },
  searchInput: {
    height: 40,
    borderBottomWidth: 1,
    borderBottomColor: theme.colors.borderColor,
    paddingHorizontal: 12,
    fontSize: 13,
    color: theme.colors.secondaryText,
    outline: 'none',
  },
  countryList: {
    maxHeight: 160,
    scrollbarWidth: 'none',
    msOverflowStyle: 'none',
    '&::-webkit-scrollbar': {
      display: 'none',
    },
    transform: 'translateZ(0)',
  },
  countryItem: {
    flexDirection: 'row',
    alignItems: 'center',
    padding: 12,
    borderBottomWidth: 0.5,
    borderBottomColor: theme.colors.borderColor,
    backfaceVisibility: 'hidden',
  },
  selectedItem: {
    backgroundColor: 'transparent',
  },
  backdrop: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'transparent',
    zIndex: 10000,
  },
  label: {
    color: theme.colors.text,
    fontSize: 13,
    fontWeight: '500',
    marginBottom: 8,
  },
  error: {
    borderColor: theme.colors.error,
  },
  errorText: {
    color: theme.colors.error,
    fontSize: 12,
    paddingHorizontal: 4,
    paddingTop: 4,
  },
  placeHolderText: {
    fontSize: 13,
    fontWeight: '400',
    color: theme.colors.placeHolderText,
  },
  pickerButton: {
    backgroundColor: theme.colors.inputBackground || '#1a1a1a',
    padding: 16,
    borderRadius: 8,
    width: '100%',
    borderBottomLeftRadius: (isOpen) => (isOpen ? 0 : 8),
    borderBottomRightRadius: (isOpen) => (isOpen ? 0 : 8),
  },
  pickerButtonOpen: {
    borderBottomWidth: 1,
    borderBottomColor: 'rgba(255, 255, 255, 0.1)',
  },
  pickerButtonText: {
    color: theme.colors.text || '#fff',
    fontSize: 16,
  },
  dropdown: {
    position: 'absolute',
    top: '100%',
    left: 0,
    right: 0,
    backgroundColor: theme.colors.inputBackground || '#1a1a1a',
    borderBottomLeftRadius: 8,
    borderBottomRightRadius: 8,
    maxHeight: 250,
    zIndex: 10001,
    elevation: 5,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.25,
    shadowRadius: 3.84,
  },
  flagImage: {
    marginRight: 10,
  },
  countryItemContent: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 10,
  },
  selectedCountryContent: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 10,
  },
  countryText: {
    color: theme.colors.text || '#fff',
    fontSize: 16,
    flex: 1,
  },
  flatListContent: {
    backgroundColor: theme.colors.onBackground,
  },
}));

export default CountryPicker;
